// @flow

import Promise from 'bluebird';
import * as d3 from 'd3';
import tinycolor from 'tinycolor2';

const imageCache = new Map();

export class ImageHelper {
    static getAverageRGB (image: HTMLImageElement) {
        // only visit every 5 pixels
        const blockSize = 5;

        // for non-supporting envs
        const defaultRGB = tinycolor({ r: 0, g: 0, b: 0 });

        const canvas: HTMLCanvasElement = document.createElement('canvas');
        const ctx: CanvasRenderingContext2D = (canvas.getContext('2d'): any);

        if (!ctx || !image) {
            return defaultRGB;
        }

        let height = canvas.height = image.naturalHeight || image.offsetHeight || image.height;
        let width = canvas.width = image.naturalWidth || image.offsetWidth || image.width;

        ctx.drawImage(image, 0, 0);

        let data;
        try {
            data = ctx.getImageData(0, 0, width, height);
        } catch (e) {
            // security error, img on diff domain
            console.log('ERROR: ', e);
            return defaultRGB;
        }

        const rgb = { r: 0, g: 0, b: 0 };
        let count = 0;

        for (let i = -4 + blockSize * 4; i < data.data.length; i += blockSize * 4) {
            ++count;
            rgb.r += data.data[i];
            rgb.g += data.data[i + 1];
            rgb.b += data.data[i + 2];
        }

        // ~~ used to floor values
        rgb.r = ~~(rgb.r / count);
        rgb.g = ~~(rgb.g / count);
        rgb.b = ~~(rgb.b / count);

        return tinycolor(rgb);
    }

    static resizeAsync (image: HTMLImageElement, width = 640) {
        return new Promise((resolve) => {
            ImageHelper.imageToCanvasAsync(image, width).then((canvas) => {
                resolve(canvas.toDataURL('image/png'));
            });
        });
    }

    static blobToBase64Async (blob) {
        return new Promise((resolve) => {
            ImageHelper.blobToImageAsync(blob).then((image: HTMLImageElement) => {
                ImageHelper.imageToCanvasAsync(image).then((canvas) => {
                    resolve(canvas.toDataURL('image/png'));
                });
            });
        });
    }

    static imageToBase64Async (image: HTMLImageElement) {
        return ImageHelper.imageToCanvasAsync(image).then((canvas) => {
            return canvas.toDataURL('image/png');
        });
    }

    static urlToBase64Async (url) {
        return new Promise((resolve) =>
            ImageHelper.urlToImageAsync(url).then(image =>
                ImageHelper.imageToCanvasAsync(image).then((canvas) => {
                    resolve(canvas.toDataURL('image/png'));
                })));
    }

    static imageToCanvasAsync (image: HTMLImageElement, width = 640) {
        return new Promise((resolve) => {
            const canvas: HTMLCanvasElement = document.createElement('canvas');
            const ctx: CanvasRenderingContext2D = (canvas.getContext('2d'): any);

            canvas.width = width;
            canvas.height = canvas.width * (image.height / image.width);

            ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

            resolve(canvas);
        });
    }

    static blobToImageAsync (blob: any) {
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                ImageHelper.urlToImageAsync(reader.result).then((image) => {
                    resolve(image);
                });
            };
            reader.readAsDataURL(blob);
        });
    }

    static svgToImageAsync (svg) {
        return new Promise((resolve, reject) => {
            const source = (new XMLSerializer()).serializeToString(svg);
            const blob = new Blob([source], { type: 'image/svg+xml;charset=utf-8' });
            ImageHelper.blobToImageAsync(blob).then(image => resolve(image));
        });
    }

    static svgToBase64 (svg) {
        const source = (new XMLSerializer()).serializeToString(svg);
        const blob = new Blob([source], { type: 'image/svg+xml;charset=utf-8' });
        return URL.createObjectURL(blob);
    }

    static urlToImageAsync (url: string) {
        return new Promise((resolve, reject) => {
            if (imageCache.has(url)) {
                resolve(imageCache.get(url));
            } else {
                const image = new Image();
                image.crossOrigin = 'Anonymous';

                image.onload = () => {
                    imageCache.set(url, image);
                    resolve(image);
                };

                image.onerror = () => {
                    resolve(image);
                };

                image.src = url;
            }
        });
    }

    static base64ToSvgAsync (url: string) {
        return new Promise((resolve, reject) => {
            const parser = new DOMParser();
            const svgDOM = parser.parseFromString(window.atob(url.replace('data:image/svg+xml;base64,', '')), 'image/svg+xml');
            const svg = d3.select(svgDOM);
            resolve(svg);
        });
    }

    static getAsync (url: string) {
        return ImageHelper.urlToImageAsync(url);
    }

    static get (url: string) {
        if (imageCache.has(url)) {
            return imageCache.get(url);
        }

        ImageHelper.urlToImageAsync(url);
        return new Image();
    }
}



// WEBPACK FOOTER //
// ./src/helpers/image.helper.js